import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';
import { cssMap } from '@atlaskit/css';

import { expValEquals } from '@confluence/feature-experiments';

import { ObjectCommentPageReactions } from './ObjectCommentPageReactions.tsx';

const commentHeaderContainerStyles = xcss({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	background: 'color.text.inverse',
	height: '64px',
	width: '100%',
	display: 'flex',
	padding: 'space.200',
	justifyContent: 'space-between',
	alignItems: 'center',
	alignSelf: 'stretch',
});

// Only reachable with the gate on — the "+N" toggle that drives `reactionsExpanded` only renders
// in the new footer layout — so gate-off always resolves to plain `commentHeaderContainerStyles`.
const commentHeaderContainerExpandedStyles = xcss({
	height: 'auto',
	flexDirection: 'column-reverse',
	alignItems: 'stretch',
	gap: 'space.100',
});

// Only applied with the gate on. The collapsed reactions row sizes itself to the pills that fit
// in the room beside the "Comments" heading, so the cluster must be allowed to shrink below its
// content size instead of overflowing the header.
const commentHeaderFitStyles = xcss({
	gap: 'space.200',
});

const commentHeaderReactionsContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	alignSelf: 'stretch',
});

const commentHeaderReactionsContainerFitStyles = xcss({
	flex: '0 1 auto',
	minWidth: '0px',
});

const i18n = defineMessages({
	objectCommentHeaderText: {
		id: 'object-comments.object-comment-header.text',
		defaultMessage: 'Comments',
		description:
			'The text is shown as a heading above the comments section on an object (e.g. a page or blog post) when the comments panel is visible. It labels the comments area for the reader.',
	},
});

const styles = cssMap({
	headerMessageStyle: {
		color: token('color.text.subtle'),
		font: token('font.heading.medium'),
		fontFamily: token('font.family.heading'),
		fontWeight: token('font.weight.medium'),
	},
});

export const ObjectCommentHeader = ({
	isOffline = false,
	showReactions,
}: {
	isOffline?: boolean;
	showReactions: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [reactionsExpanded, setReactionsExpanded] = useState(false);

	return (
		<Box
			xcss={[
				commentHeaderContainerStyles,
				expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true) &&
					commentHeaderFitStyles,
				reactionsExpanded && commentHeaderContainerExpandedStyles,
			]}
		>
			<Heading size="medium" as="h2">
				<Box xcss={styles.headerMessageStyle}>{formatMessage(i18n.objectCommentHeaderText)}</Box>
			</Heading>
			{showReactions && (
				<Box
					xcss={[
						commentHeaderReactionsContainerStyles,
						expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true) &&
							commentHeaderReactionsContainerFitStyles,
					]}
				>
					<ObjectCommentPageReactions
						isOffline={isOffline}
						onExpandedChange={setReactionsExpanded}
					/>
				</Box>
			)}
		</Box>
	);
};
